import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download } from 'lucide-react';
import { AppIcon } from '@/components/icons/AppIcon';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    let timeoutId: number | undefined;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Show prompt after 5 seconds (not to disturb immediately)
      timeoutId = window.setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      // Clear timeout if component unmounts before 5 seconds
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    console.log(`Scelta utente installazione: ${outcome}`);

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-20 left-4 right-4 md:bottom-4 md:left-auto md:right-4 md:max-w-md bg-white rounded-2xl shadow-2xl border-2 border-wine-burgundy z-[70] overflow-hidden"
      >
        {/* Wine-themed header */}
        <div className="bg-[#95102d] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <AppIcon className="w-8 h-8" />
            <span className="font-serif font-bold text-lg">Installa MyWine</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Chiudi"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Aggiungi la tua cantina digitale alla home screen per un accesso rapido e un'esperienza migliore!
          </p>

          <div className="space-y-2 mb-4">
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <span>✅</span>
              <span>Accesso istantaneo dalla home</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <span>✅</span>
              <span>Esperienza app nativa</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <span>✅</span>
              <span>Nessuna barra browser</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <span>✅</span>
              <span>Sempre aggiornata</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleInstall}
              className="flex-1 bg-wine-burgundy hover:bg-wine-merlot text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
            >
              <Download size={20} />
              <span>Installa</span>
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
            >
              Più tardi
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
